import type {ThunkAction, Dispatch} from 'src/reducers';
import type {ApiOptions} from 'src/utils/redux-api';
import * as reduxApi from 'src/utils/redux-api-bff';
import {thunkify as flow} from 'src/utils/thunks';
import {cached, fetching, key} from 'src/utils/redux';
import logger from 'src/utils/logger';

import type {
  DataAuditDashboardCard,
  DataAuditEntityType,
  DataAuditAuditTableApiResponse,
  DataAuditAuditTableParams,
  DataAuditAuditDetailApi,
  DataAuditAuditByAtsIdApiResponse,
  DataAuditQueuedTableApiResponse,
  DataAuditQueuedTableParams,
  DataAuditWritebackBreakdownGraphsState,
  DataAuditWritebackDashboardCardApi,
  DataAuditWritebackGraphApi,
  DataAuditWritebackType,
  DataAuditSupportedFieldApi,
} from 'src/components/data-audit/types';
import {
  aggregateWritebackDashboardCards,
  buildDataAuditAuditTableQuery,
  buildDataAuditGraphQuery,
  buildDataAuditQueuedTableQuery,
  getDataAuditQueuedTableEndpoint,
  getWritebackAuditStatusApiType,
  normalizeDataAuditAuditByAtsIdResponse,
} from 'src/components/data-audit/utils/data-audit';


export const RECEIVE_DASHBOARD_CARDS =
  'analytics-data-audit/receive-dashboard-cards';

export const RECEIVE_WRITEBACK_AUDIT_STATUSES =
  'analytics-data-audit/receive-writeback-audit-statuses';

export const RECEIVE_ENTITY_TYPES = 'analytics-data-audit/receive-entity-types';

export const RECEIVE_NOTE_TYPES = 'analytics-data-audit/receive-note-types';

export const RECEIVE_AUDIT_TABLE_ENTRIES =
  'analytics-data-audit/receive-audit-table-entries';

export const RECEIVE_QUEUED_TABLE_ENTRIES =
  'analytics-data-audit/receive-queued-table-entries';

export const RECEIVE_WRITEBACK_BREAKDOWN_GRAPHS =
  'analytics-data-audit/receive-writeback-breakdown-graphs';

export const RECEIVE_SUPPORTED_FIELDS =
  'analytics-data-audit/receive-supported-fields';

export const RECEIVE_AUDIT_DETAIL = 'analytics-data-audit/receive-audit-detail';

export const CLEAR_AUDIT_DETAIL = 'analytics-data-audit/clear-audit-detail';

export const RECEIVE_AUDIT_BY_ATS_ID =
  'analytics-data-audit/receive-audit-by-ats-id';

export const CLEAR_AUDIT_BY_ATS_ID =
  'analytics-data-audit/clear-audit-by-ats-id';

type ReceiveDashboardCardsAction = {
  type: typeof RECEIVE_DASHBOARD_CARDS;
  payload: DataAuditDashboardCard;
};

type ReceiveWritebackAuditStatusesAction = {
  type: typeof RECEIVE_WRITEBACK_AUDIT_STATUSES;
  payload: {
    writebackType: DataAuditWritebackType;
    statuses: Array<string>;
  };
};

type ReceiveEntityTypesAction = {
  type: typeof RECEIVE_ENTITY_TYPES;
  payload: Array<DataAuditEntityType>;
};

type ReceiveNoteTypesAction = {
  type: typeof RECEIVE_NOTE_TYPES;
  payload: Array<string>;
};

type ReceiveAuditTableEntriesAction = {
  type: typeof RECEIVE_AUDIT_TABLE_ENTRIES;
  payload: {
    response: DataAuditAuditTableApiResponse;
    params: DataAuditAuditTableParams;
  };
};

type ReceiveQueuedTableEntriesAction = {
  type: typeof RECEIVE_QUEUED_TABLE_ENTRIES;
  payload: {
    response: DataAuditQueuedTableApiResponse;
    params: DataAuditQueuedTableParams;
  };
};

type ReceiveWritebackBreakdownGraphsAction = {
  type: typeof RECEIVE_WRITEBACK_BREAKDOWN_GRAPHS;
  payload: DataAuditWritebackBreakdownGraphsState;
};

type ReceiveSupportedFieldsAction = {
  type: typeof RECEIVE_SUPPORTED_FIELDS;
  payload: Array<DataAuditSupportedFieldApi>;
};

type ReceiveAuditDetailAction = {
  type: typeof RECEIVE_AUDIT_DETAIL;
  payload: {
    detail: DataAuditAuditDetailApi;
    auditId: string;
    writebackType: DataAuditWritebackType;
  };
};

type ClearAuditDetailAction = {
  type: typeof CLEAR_AUDIT_DETAIL;
};

type ReceiveAuditByAtsIdAction = {
  type: typeof RECEIVE_AUDIT_BY_ATS_ID;
  payload: {
    nodes: DataAuditAuditByAtsIdApiResponse;
    atsId: string;
    writebackType: DataAuditWritebackType;
  };
};

type ClearAuditByAtsIdAction = {
  type: typeof CLEAR_AUDIT_BY_ATS_ID;
};

export type DataAuditActions =
  | ReceiveDashboardCardsAction
  | ReceiveWritebackAuditStatusesAction
  | ReceiveEntityTypesAction
  | ReceiveNoteTypesAction
  | ReceiveAuditTableEntriesAction
  | ReceiveQueuedTableEntriesAction
  | ReceiveWritebackBreakdownGraphsAction
  | ReceiveSupportedFieldsAction
  | ReceiveAuditDetailAction
  | ClearAuditDetailAction
  | ReceiveAuditByAtsIdAction
  | ClearAuditByAtsIdAction;

const receiveDashboardCards = (
  cards: DataAuditDashboardCard,
): ReceiveDashboardCardsAction => ({
  type: RECEIVE_DASHBOARD_CARDS,
  payload: cards,
});

const receiveWritebackAuditStatuses = (
  writebackType: DataAuditWritebackType,
  statuses: Array<string>,
): ReceiveWritebackAuditStatusesAction => ({
  type: RECEIVE_WRITEBACK_AUDIT_STATUSES,
  payload: {
    writebackType,
    statuses,
  },
});

const receiveEntityTypes = (
  entityTypes: Array<DataAuditEntityType>,
): ReceiveEntityTypesAction => ({
  type: RECEIVE_ENTITY_TYPES,
  payload: entityTypes,
});

const receiveNoteTypes = (
  noteTypes: Array<string>,
): ReceiveNoteTypesAction => ({
  type: RECEIVE_NOTE_TYPES,
  payload: noteTypes,
});

const receiveAuditTableEntries = (
  response: DataAuditAuditTableApiResponse,
  params: DataAuditAuditTableParams,
): ReceiveAuditTableEntriesAction => ({
  type: RECEIVE_AUDIT_TABLE_ENTRIES,
  payload: {
    response,
    params,
  },
});

const receiveQueuedTableEntries = (
  response: DataAuditQueuedTableApiResponse,
  params: DataAuditQueuedTableParams,
): ReceiveQueuedTableEntriesAction => ({
  type: RECEIVE_QUEUED_TABLE_ENTRIES,
  payload: {
    response,
    params,
  },
});

const receiveWritebackBreakdownGraphs = (
  graphs: DataAuditWritebackBreakdownGraphsState,
): ReceiveWritebackBreakdownGraphsAction => ({
  type: RECEIVE_WRITEBACK_BREAKDOWN_GRAPHS,
  payload: graphs,
});

const receiveSupportedFields = (
  fields: Array<DataAuditSupportedFieldApi>,
): ReceiveSupportedFieldsAction => ({
  type: RECEIVE_SUPPORTED_FIELDS,
  payload: fields,
});

const receiveAuditDetail = (
  detail: DataAuditAuditDetailApi,
  auditId: string,
  writebackType: DataAuditWritebackType,
): ReceiveAuditDetailAction => ({
  type: RECEIVE_AUDIT_DETAIL,
  payload: {
    detail,
    auditId,
    writebackType,
  },
});

export const clearAuditDetail = (): ClearAuditDetailAction => ({
  type: CLEAR_AUDIT_DETAIL,
});

const receiveAuditByAtsId = (
  nodes: DataAuditAuditByAtsIdApiResponse,
  atsId: string,
  writebackType: DataAuditWritebackType,
): ReceiveAuditByAtsIdAction => ({
  type: RECEIVE_AUDIT_BY_ATS_ID,
  payload: {
    nodes,
    atsId,
    writebackType,
  },
});

export const clearAuditByAtsId = (): ClearAuditByAtsIdAction => ({
  type: CLEAR_AUDIT_BY_ATS_ID,
});

const DATA_AUDIT_API_OPTIONS: ApiOptions = {
  apiPath: '/bff/api/v1/',
};

export const getDataAuditDashboardCards = flow(
  key((params: {from?: string; to?: string}) =>
    ['data-audit-dashboard-cards', params.from ?? '', params.to ?? ''].join(
      ':',
    ),
  ),
  fetching(),
)((params: {from?: string; to?: string}) => async (dispatch: Dispatch) => {
  try {
    const query = buildDataAuditGraphQuery(params);

    const response = (await dispatch(
      reduxApi.get('writebacks-summary/metrics', query, DATA_AUDIT_API_OPTIONS),
    )) as Array<DataAuditWritebackDashboardCardApi>;

    dispatch(receiveDashboardCards(aggregateWritebackDashboardCards(response)));
  } catch (error) {
    logger.error('Failed to fetch data audit dashboard cards', error);
    throw error;
  }
});

export const getWritebackAuditStatuses = (
  writebackType: DataAuditWritebackType,
): ThunkAction => {
  return async (dispatch) => {
    try {
      const apiWritebackType = getWritebackAuditStatusApiType(writebackType);
      const response = (await dispatch(
        reduxApi.get(
          `writebacks/statuses?writebackType=${apiWritebackType}`,
          undefined,
          DATA_AUDIT_API_OPTIONS,
        ),
      )) as Array<string>;

      dispatch(receiveWritebackAuditStatuses(writebackType, response));
    } catch (error) {
      logger.error('Failed to fetch writeback audit statuses', error);
      throw error;
    }
  };
};

export const getDataAuditEntityTypes: () => ThunkAction = flow(
  key(() => 'data-audit-entity-types'),
  cached((entityTypes) => receiveEntityTypes(entityTypes ?? [])),
  fetching(),
)(
  () => (dispatch) =>
    dispatch(
      reduxApi.get(
        'writeback/owb/entity-types',
        undefined,
        DATA_AUDIT_API_OPTIONS,
      ),
    ).catch((error) => {
      logger.error('Failed to fetch data audit entity types', error);
      throw error;
    }),
);

export const getDataAuditNoteTypes: () => ThunkAction = flow(
  key(() => 'data-audit-note-types'),
  cached((noteTypes) => receiveNoteTypes(noteTypes ?? [])),
  fetching(),
)(
  () => (dispatch) =>
    dispatch(
      reduxApi.get('writebacks/noteTypes', undefined, DATA_AUDIT_API_OPTIONS),
    ).catch((error) => {
      logger.error('Failed to fetch data audit note types', error);
      throw error;
    }),
);

export const getDataAuditAuditTableEntries = flow(
  key((params: DataAuditAuditTableParams) =>
    [
      'data-audit-audit-table',
      params.writebackType,
      params.page,
      params.size,
      params.status ?? '',
      params.source ?? '',
      params.entityType ?? '',
      params.noteType ?? '',
      params.from ?? '',
      params.to ?? '',
      params.sort ?? '',
      params.search ?? '',
    ].join(':'),
  ),
  fetching(),
)(
  (params: DataAuditAuditTableParams) => (dispatch: Dispatch) =>
    dispatch(
      reduxApi.get(
        'writebacks-summary/audit',
        buildDataAuditAuditTableQuery({
          ...params,
          page: params.page - 1,
        }),
        DATA_AUDIT_API_OPTIONS,
      ),
    )
      .then((response: DataAuditAuditTableApiResponse) => {
        dispatch(receiveAuditTableEntries(response, params));
      })
      .catch((error) => {
        logger.error('Failed to fetch data audit audit table entries', error);
        throw error;
      }),
);

export const getDataAuditQueuedTableEntries = flow(
  key((params: DataAuditQueuedTableParams) =>
    [
      'data-audit-queued-table',
      params.writebackType,
      params.page,
      params.size,
      params.source ?? '',
      params.atsEntityType ?? '',
      params.noteType ?? '',
      params.search ?? '',
    ].join(':'),
  ),
  fetching(),
)(
  (params: DataAuditQueuedTableParams) => (dispatch: Dispatch) =>
    dispatch(
      reduxApi.get(
        getDataAuditQueuedTableEndpoint(),
        buildDataAuditQueuedTableQuery({
          ...params,
          page: params.page - 1,
        }),
        DATA_AUDIT_API_OPTIONS,
      ),
    )
      .then((response: DataAuditQueuedTableApiResponse) => {
        dispatch(receiveQueuedTableEntries(response, params));
      })
      .catch((error) => {
        logger.error('Failed to fetch data audit queued table entries', error);
        throw error;
      }),
);

const WRITEBACK_BREAKDOWN_GRAPH_TYPES: Array<DataAuditWritebackType> = [
  'nwb',
  'owb',
  'fwb',
];

export const getDataAuditWritebackBreakdownGraphs = flow(
  key((params: {from?: string; to?: string}) =>
    [
      'data-audit-writeback-breakdown-graphs',
      params.from ?? '',
      params.to ?? '',
    ].join(':'),
  ),
  fetching(),
)((params: {from?: string; to?: string}) => async (dispatch: Dispatch) => {
  try {
    const query = buildDataAuditGraphQuery(params);

    const responses = await Promise.all(
      WRITEBACK_BREAKDOWN_GRAPH_TYPES.map((writebackType) =>
        dispatch(
          reduxApi.get(
            'writebacks-summary/source-breakdown',
            {
              ...query,
              writebackType: writebackType.toUpperCase(),
            },
            DATA_AUDIT_API_OPTIONS,
          ),
        ),
      ),
    );

    const graphs = WRITEBACK_BREAKDOWN_GRAPH_TYPES.reduce(
      (acc, writebackType, index) => ({
        ...acc,
        [writebackType]:
          (responses[index] as DataAuditWritebackGraphApi) ?? null,
      }),
      {
        nwb: null,
        owb: null,
        fwb: null,
      } as DataAuditWritebackBreakdownGraphsState,
    );

    dispatch(receiveWritebackBreakdownGraphs(graphs));
  } catch (error) {
    logger.error(
      'Failed to fetch data audit writeback breakdown graphs',
      error,
    );
    throw error;
  }
});

export const getDataAuditSupportedFields: () => ThunkAction = flow(
  key(() => 'data-audit-supported-fields'),
  cached((fields) => receiveSupportedFields(fields ?? [])),
  fetching(),
)(
  () => (dispatch) =>
    dispatch(
      reduxApi.get(
        'writeback/owb/field-meta',
        undefined,
        DATA_AUDIT_API_OPTIONS,
      ),
    ).catch((error) => {
      logger.error('Failed to fetch data audit supported fields', error);
      throw error;
    }),
);

export const getDataAuditAuditDetail = flow(
  key(
    (auditId: string, writebackType: DataAuditWritebackType) =>
      `data-audit-audit-detail:${auditId}:${writebackType}`,
  ),
  fetching(),
)(
  (auditId: string, writebackType: DataAuditWritebackType) =>
    (dispatch: Dispatch) =>
      dispatch(
        reduxApi.get(
          `writebacks-summary/audit/${encodeURIComponent(auditId)}`,
          {writebackType: writebackType.toUpperCase()},
          DATA_AUDIT_API_OPTIONS,
        ),
      )
        .then((response: DataAuditAuditDetailApi) => {
          dispatch(receiveAuditDetail(response, auditId, writebackType));
        })
        .catch((error) => {
          logger.error('Failed to fetch data audit audit detail', error);
          throw error;
        }),
);

export const getDataAuditAuditByAtsId = flow(
  key(
    (atsId: string, writebackType: DataAuditWritebackType) =>
      `data-audit-audit-by-ats-id:${atsId}:${writebackType}`,
  ),
  fetching(),
)(
  (atsId: string, writebackType: DataAuditWritebackType) =>
    (dispatch: Dispatch) =>
      dispatch(
        reduxApi.get(
          'writebacks-summary/detailed-audit',
          {
            externalEntityId: atsId,
            writebackType: writebackType.toUpperCase(),
          },
          DATA_AUDIT_API_OPTIONS,
        ),
      )
        .then((response: unknown) => {
          dispatch(
            receiveAuditByAtsId(
              normalizeDataAuditAuditByAtsIdResponse(response),
              atsId,
              writebackType,
            ),
          );
        })
        .catch((error) => {
          logger.error('Failed to fetch data audit by ATS ID', error);
          throw error;
        }),
);
